import { useNavigate } from 'react-router-dom';
import { CANDIDATES } from '../../../../Routing/paths';
import { StyledCell } from './styles';
import { Chip, IconButton, Tooltip } from '@mui/material';
import NextWeekIcon from '@mui/icons-material/NextWeek';
import { AssignCandidateDialog } from './Components/AssignCandidateDialog';
import { useCallback, useState } from 'react';
import { t } from 'i18next';
import React from 'react';
import { DashboardScrollTable } from '../../../../Components/DashboardTable/DashboardScrollTable';
import { useScrollStore } from '../../../../Stores/useGeneralAppStore';

interface Props {
  rows: any[];
  loadMore: () => void;
  loading: boolean;
  hasMore: boolean;
  totalItems: number;
  onAssignSuccess: () => void;
}

const headCells: HeadCell[] = [
  {
    id: 'fullName',
    numeric: true,
    disablePadding: false,
    label: 'Name',
  },
  {
    id: 'id',
    numeric: false,
    disablePadding: true,
    label: 'Id',
  },
  {
    id: 'linkedin',
    numeric: true,
    disablePadding: false,
    label: 'LinkedIn',
  },
  {
    id: 'referral.name',
    numeric: false,
    disablePadding: true,
    label: 'Referral',
  },
  {
    id: 'position',
    numeric: true,
    disablePadding: false,
    label: 'Active Positions',
  },
  {
    id: 'lastPosition',
    numeric: true,
    disablePadding: false,
    label: 'Last Position',
  },
  {
    id: 'seniority',
    numeric: true,
    disablePadding: false,
    label: 'Seniority',
  },
  {
    id: 'englishLevel',
    numeric: true,
    disablePadding: false,
    label: 'English Level',
  },
  {
    id: 'skills',
    numeric: true,
    disablePadding: false,
    label: 'Skills',
  },
  {
    id: 'Job',
    numeric: true,
    disablePadding: false,
    label: 'Job',
  },
];

export const CandidateTable = ({
  rows,
  loadMore,
  loading,
  hasMore,
  totalItems,
  onAssignSuccess,
}: Props) => {
  const navigate = useNavigate();
  const { setSelectedSourcePage, setSelectedScrollPosition } = useScrollStore();

  const [showAssignCandidatesModal, setShowAssignCandidatesModal] = useState(false);
  const [candidateToAssignId, setCandidateToAssignId] = useState<string | undefined>();

  const handleLoadMore = useCallback(() => {
    if (hasMore && !loading) {
      loadMore();
    }
  }, [hasMore, loading, loadMore]);

  const handleRowClick = (index: number, link: string) => {
    setSelectedSourcePage(CANDIDATES);
    setSelectedScrollPosition(index);
    navigate(link);
  };

  const rowContent = (index: number, row: any) => {
    const labelId = `enhanced-table-checkbox-${index}`;
    const candidateLink = `${CANDIDATES}/${row.id}`;

    return (
      <React.Fragment>
        <StyledCell onClick={() => handleRowClick(index, candidateLink)}>{row.fullName}</StyledCell>
        <StyledCell id={labelId} onClick={() => handleRowClick(index, candidateLink)} scope="row">
          {row.id}
        </StyledCell>
        <StyledCell onClick={() => handleRowClick(index, candidateLink)}>
          {row.linkedin && (
            <a
              href={row.linkedin ?? ''}
              onClick={(e) => e.stopPropagation()}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('common.go_to_profile')}
            </a>
          )}
        </StyledCell>
        <StyledCell onClick={() => handleRowClick(index, candidateLink)}>
          {row.referral?.name ? row.referral?.name : '-'}
        </StyledCell>
        <StyledCell onClick={() => handleRowClick(index, candidateLink)}>
          {Array.isArray(row.position)
            ? row.position.map((position: string) => (
                <div key={position} style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: '8px' }}>•</span>
                  {position}
                </div>
              ))
            : row.position || '-'}
        </StyledCell>
        <StyledCell onClick={() => handleRowClick(index, candidateLink)}>
          {row.lastPosition ? row.lastPosition : '-'}
        </StyledCell>
        <StyledCell onClick={() => handleRowClick(index, candidateLink)}>{row.seniority}</StyledCell>
        <StyledCell onClick={() => handleRowClick(index, candidateLink)}>
          {row.languageLevel || '-'}
        </StyledCell>
        <StyledCell onClick={() => handleRowClick(index, candidateLink)}>
          {row.skills && row.skills.length > 0 ? (
            <>
              {row.skills.slice(0, 2).map((skill: string) => (
                <Chip label={skill} sx={{ marginRight: 1 }} key={skill} />
              ))}
              {row.skills.length > 2 && (
                <Chip label={`+${row.skills.length - 2}`} sx={{ marginRight: 1 }} />
              )}
            </>
          ) : (
            '-'
          )}
        </StyledCell>
        <StyledCell onClick={() => handleRowClick(index, candidateLink)}>
          <Tooltip title={t('jobs.assign.tooltip')}>
            <IconButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCandidateToAssignId(row.id);
                setShowAssignCandidatesModal(true);
              }}
            >
              <NextWeekIcon />
            </IconButton>
          </Tooltip>
        </StyledCell>
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <DashboardScrollTable
        headerCells={headCells}
        rows={rows}
        loadMore={handleLoadMore}
        hasMore={hasMore}
        rowContent={rowContent}
        totalItems={totalItems}
        pageNavigation={CANDIDATES}
      />
      <AssignCandidateDialog
        isOpen={showAssignCandidatesModal}
        handleClose={() => setShowAssignCandidatesModal(false)}
        candidateId={candidateToAssignId!}
        onAssignSuccess={onAssignSuccess}
      />
    </React.Fragment>
  );
};
